import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { SatelliteStreamChip } from "@/components/symin/SatelliteStreamChip";
import { AreaSelect } from "@/components/symin/AreaSelect";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Planet, Images, Info, Warning, ArrowSquareOut, CircleNotch } from "@phosphor-icons/react";

const GROUPS = [
  {
    group: "Spectral & Alteration", accent: "#A855F7", layers: [
      { key: "iron_oxide", label: "Iron-Oxide / Gossan", desc: "Ferric iron-oxide staining (hematite, jarosite, limonite) from the red/blue band ratio - a primary vector toward gossanous and oxidised mineralisation." },
      { key: "clay_hydroxyl", label: "Clay / Hydroxyl", desc: "Al-OH clay & sericite (SWIR) mapping phyllic and argillic hydrothermal alteration that commonly haloes mineralised systems." },
      { key: "ferrous_iron", label: "Ferrous (Mafic)", desc: "Ferrous-iron response (SWIR/NIR) highlighting mafic-ultramafic and magnetite-bearing rocks and chloritic alteration." },
      { key: "composite", label: "Composite Spectral", desc: "Commodity-weighted composite combining the diagnostic alteration indices into a single spectral anomaly surface." },
      { key: "pca", label: "PCA / Alteration", desc: "Principal-component and feature-oriented enhancement isolating subtle alteration signatures from background spectral variance." },
    ],
  },
  {
    group: "Prospectivity", accent: "#C20511", layers: [
      { key: "prospectivity", label: "Mineral Prospectivity", desc: "Fused multi-sensor prospectivity (0-100) integrating spectral, structural and terrain evidence, strictly clipped to the licence." },
      { key: "confidence", label: "Data Confidence", desc: "Per-pixel data confidence (0-100) - high where several independent sensors coincide on clear exposure." },
      { key: "target", label: "Priority Targets", desc: "Ranked, mutually-exclusive target zones delineated from coincident spectral and structural evidence." },
    ],
  },
  {
    group: "Terrain", accent: "#10B981", layers: [
      { key: "dem", label: "Elevation (DEM)", desc: "Copernicus GLO-30 digital elevation model - the base for all terrain and structural-relief products." },
      { key: "slope", label: "Slope", desc: "Terrain slope from the DEM, indicating outcrop favourability and structural breaks." },
      { key: "hillshade", label: "Hillshade Relief", desc: "Shaded relief emphasising subtle topographic lineaments and structural fabric." },
    ],
  },
  {
    group: "Structure", accent: "#38BDF8", layers: [
      { key: "structural", label: "Structural Lineaments", desc: "Automatically extracted lineaments interpreted as faults and fractures - the controls that focus fluid flow and mineralisation." },
      { key: "density", label: "Lineament Density", desc: "Fracture-intensity surface highlighting structurally complex corridors (dilational jogs and intersections)." },
      { key: "sar", label: "SAR Structure", desc: "Radar backscatter structural enhancement, independent of optical cloud cover." },
    ],
  },
  {
    group: "Imagery", accent: "#00B4D8", layers: [
      { key: "base", label: "Satellite Base", desc: "True-colour Sentinel-2 mosaic of the licence, cloud-screened and clipped to the boundary." },
      { key: "geology", label: "Geological Setting", desc: "Interpreted structural-lithological framework showing the host controls relevant to the target commodity." },
    ],
  },
];
const LAYER_META = Object.fromEntries(GROUPS.flatMap((g) => g.layers.map((l) => [l.key, { ...l, accent: g.accent }])));

export default function RemoteSensingViewer() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState(null);
  const [layer, setLayer] = useState("prospectivity");
  const [img, setImg] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | ready | error | pending
  const cache = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/projects/${id}/map-data`);
        setProject(r.data.project);
        setAreas(r.data.areas || []);
        const firstComplete = (r.data.areas || []).find((a) => a.status === "complete") || (r.data.areas || [])[0];
        if (firstComplete) setArea(firstComplete.area_id);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [id]);

  const activeArea = useMemo(() => areas.find((a) => a.area_id === area), [areas, area]);

  useEffect(() => {
    if (!area || !layer) return;
    if (activeArea && activeArea.status !== "complete") { setState("pending"); setImg(null); return; }
    const ck = `${area}:${layer}`;
    if (cache.current[ck]) { setImg(cache.current[ck]); setState("ready"); return; }
    let cancelled = false;
    setState("loading"); setImg(null);
    api.get(`/projects/${id}/areas/${area}/map/${layer}?inline=1`)
      .then((r) => { if (cancelled) return; cache.current[ck] = r.data.uri; setImg(r.data.uri); setState("ready"); })
      .catch((e) => { if (cancelled) return; setState("error"); const s = e?.response?.status; if (s !== 404) toast.error(apiError(e)); });
    return () => { cancelled = true; };
  }, [id, area, layer, activeArea]);

  const meta = LAYER_META[layer] || {};

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="rs" />
      <div className="ml-[64px] flex flex-col h-screen">
        <header className="h-[52px] shrink-0 bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-5">
          <Planet size={18} style={{ color: "#A855F7" }} className="shrink-0" />
          <h1 className="font-display font-semibold text-sm sm:text-lg tracking-tight truncate min-w-0" data-testid="eo-title">Earth Observation</h1>
          <span className="text-xs text-muted-foreground truncate hidden lg:block">{project?.name}</span>
          <SatelliteStreamChip testid="eo-stream-chip" />
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <AreaSelect areas={areas} value={area} onChange={setArea} accent="#A855F7" testid="eo-area-select" />
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => nav(`/projects/${id}/command`)} data-testid="eo-back-command">
              <ArrowSquareOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Command Centre</span>
            </Button>
          </div>
        </header>

        <div className="flex-1 flex min-h-0 flex-col lg:flex-row">
          {/* Layer selector */}
          <aside className="w-full lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-border bg-card overflow-y-auto max-h-[34vh] lg:max-h-none" data-testid="eo-layer-panel">
            {GROUPS.map((g) => (
              <div key={g.group} className="px-3 py-3 border-b border-border">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: g.accent }} />{g.group}
                </div>
                <div className="space-y-0.5">
                  {g.layers.map((l) => (
                    <button key={l.key} data-testid={`eo-layer-${l.key}`} onClick={() => setLayer(l.key)}
                      className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-colors ${layer === l.key ? "text-foreground" : "text-muted-foreground hover:bg-accent/50"}`}
                      style={layer === l.key ? { background: `${g.accent}1F`, boxShadow: `inset 2px 0 0 ${g.accent}` } : undefined}>
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </aside>

          {/* Viewer */}
          <div className="flex-1 min-w-0 bg-[#04070B] flex flex-col">
            <div className="flex-1 min-h-0 grid place-items-center p-4 relative" data-testid="eo-canvas">
              {state === "loading" && <div className="flex flex-col items-center gap-3 text-muted-foreground"><CircleNotch size={28} className="animate-spin" style={{ color: meta.accent }} /><span className="text-sm">Rendering {meta.label}…</span></div>}
              {state === "pending" && <div className="flex flex-col items-center gap-2 text-muted-foreground max-w-sm text-center"><Warning size={26} style={{ color: "#FBBF24" }} /><span className="text-sm">This licence area has not been processed yet. Process it in Data &amp; Setup to view its Earth-observation layers.</span></div>}
              {state === "error" && <div className="flex flex-col items-center gap-2 text-muted-foreground max-w-sm text-center"><Warning size={26} style={{ color: "#F43F5E" }} /><span className="text-sm">This layer is not available for the selected area yet. Generate the area to render it.</span></div>}
              {state === "ready" && img && (
                <img src={img} alt={meta.label} data-testid="eo-image"
                  className="max-h-full max-w-full object-contain rounded-md border border-border shadow-2xl" />
              )}
            </div>
            {/* Layer description */}
            <div className="shrink-0 border-t border-border bg-card px-5 py-3 flex items-start gap-3" data-testid="eo-layer-desc">
              <Info size={16} style={{ color: meta.accent }} className="shrink-0 mt-0.5" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display font-semibold text-sm">{meta.label}</span>
                  <Badge variant="outline" className="text-[9px]">{activeArea?.name}</Badge>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5 max-w-3xl">{meta.desc}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
